import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    BusFilters,
    CapacityThresholds,
    ColorMode,
    getDefaultBusFilter,
    getDefaultBusFilterAndOverrideWithParams,
    LocationFilter,
    MaybeMaxCosts
} from "types/busFilterTypes";
import { ScopeName } from "types/scope";
import {
    convertScreeningViewToBusFilters,
    ScreeningView
} from "types/screeningViewType";
import { Voltage } from "types/voltageTypes";

interface BusFiltersConfiguration {
    busFilters: BusFilters;
    updateScope: (newScope: ScopeName) => void;

    setVoltages: (voltages: ReadonlyArray<Voltage>) => void;
    setOwners: (owners: ReadonlyArray<string> | null) => void;

    updateMaybeMaxAllocatedCosts: (
        maybeMaxAllocatedCosts: MaybeMaxCosts
    ) => void;
    updateMaybeMaxTotalCosts: (maybeMaxTotalCosts: MaybeMaxCosts) => void;
    toggleIncludePreExistingConstraints: () => void;
    toggleHideLockedBuses: () => void;
    toggleHideLowConfidenceBuses: () => void;
    toggleHideOutOfRegionBuses: () => void;

    updateCapacityThresholds: (capacityThresholds: CapacityThresholds) => void;
    updateColorMode: (colorMode: ColorMode) => void;
    updateConstraintCountThresholds: (
        constraintCountThresholds: CapacityThresholds
    ) => void;

    updateLocationFilter: (filter: LocationFilter) => void;

    resetToDefaults: () => void;

    filtersDisabled: boolean;
}

const useCreateBusFiltersConfiguration = (): BusFiltersConfiguration => {
    const { region, generator, acceptableScopes } = useMapViewRoutingMetadata();
    const [queryParams] = useSearchParams();

    const [busFilters, setBusFilters] = useState(() =>
        getDefaultBusFilterAndOverrideWithParams(
            region,
            generator,
            acceptableScopes,
            queryParams
        )
    );

    const updateScope = (newScope: ScopeName) => {
        setBusFilters({
            ...busFilters,
            scope: newScope
        });
    };

    const updateMaybeMaxAllocatedCosts = (
        maybeMaxAllocatedCosts: MaybeMaxCosts
    ) => {
        setBusFilters({ ...busFilters, maybeMaxAllocatedCosts });
    };

    const updateMaybeMaxTotalCosts = (maybeMaxTotalCosts: MaybeMaxCosts) => {
        setBusFilters({
            ...busFilters,
            maybeMaxTotalCosts: maybeMaxTotalCosts
        });
    };

    const toggleIncludePreExistingConstraints = () => {
        setBusFilters({
            ...busFilters,
            includePreExistingConstraints:
                !busFilters.includePreExistingConstraints
        });
    };

    const toggleHideLockedBuses = () => {
        setBusFilters({
            ...busFilters,
            hideLockedBuses: !busFilters.hideLockedBuses
        });
    };

    const toggleHideLowConfidenceBuses = () => {
        setBusFilters({
            ...busFilters,
            hideLowConfidenceBuses: !busFilters.hideLowConfidenceBuses
        });
    };

    const toggleHideOutOfRegionBuses = () => {
        setBusFilters({
            ...busFilters,
            hideOutOfRegionBuses: !busFilters.hideOutOfRegionBuses
        });
    };

    const updateCapacityThresholds = (
        capacityThresholds: CapacityThresholds
    ) => {
        setBusFilters({
            ...busFilters,
            capacityThresholds
        });
    };

    const updateColorMode = (colorMode: ColorMode) => {
        setBusFilters({
            ...busFilters,
            colorMode
        });
    };

    const updateConstraintCountThresholds = (
        constraintCountThresholds: CapacityThresholds
    ) => {
        setBusFilters({
            ...busFilters,
            constraintCountThresholds
        });
    };

    const updateLocationFilter = (locationFilter: LocationFilter) => {
        setBusFilters({ ...busFilters, locationFilter });
    };

    const resetToDefaults = () => {
        setBusFilters(getDefaultBusFilter(region, generator, acceptableScopes));
    };

    return {
        busFilters,
        updateScope,
        setVoltages: getSetVoltages(busFilters, setBusFilters),
        setOwners: getSetOwners(busFilters, setBusFilters),
        updateMaybeMaxAllocatedCosts,
        updateMaybeMaxTotalCosts,
        toggleIncludePreExistingConstraints,
        toggleHideLockedBuses,
        toggleHideLowConfidenceBuses,
        toggleHideOutOfRegionBuses,
        updateCapacityThresholds,
        updateColorMode,
        updateConstraintCountThresholds,
        updateLocationFilter,
        resetToDefaults,
        filtersDisabled: false
    };
};

// This is used when we want to load a screening view. We don't actually want to be able to edit any filters
// except for the voltage filters
const useCreateBusFiltersFromScreeningView = (
    maybeScreeningViewData: ScreeningView
): BusFiltersConfiguration => {
    const [busFilters, setBusFilters] = useState(() =>
        convertScreeningViewToBusFilters(maybeScreeningViewData)
    );
    return {
        busFilters,
        updateScope: () => {
            return;
        },
        setVoltages: getSetVoltages(busFilters, setBusFilters),
        setOwners: getSetOwners(busFilters, setBusFilters),
        updateMaybeMaxAllocatedCosts: () => {
            return;
        },
        updateMaybeMaxTotalCosts: () => {
            return;
        },
        toggleIncludePreExistingConstraints: () => {
            return;
        },
        toggleHideLockedBuses: () => {
            return;
        },
        toggleHideLowConfidenceBuses: () => {
            return;
        },
        toggleHideOutOfRegionBuses: () => {
            return;
        },
        updateCapacityThresholds: () => {
            return;
        },
        updateColorMode: () => {
            return;
        },
        updateConstraintCountThresholds: () => {
            return;
        },
        updateLocationFilter: () => {
            return;
        },
        resetToDefaults: () => {
            return;
        },
        filtersDisabled: true
    };
};

const getSetVoltages = (
    busFilters: BusFilters,
    setBusFilters: (busFilters: BusFilters) => void
): ((voltages: ReadonlyArray<Voltage>) => void) => {
    return (voltages: ReadonlyArray<Voltage>) => {
        setBusFilters({
            ...busFilters,
            voltages
        });
    };
};

const getSetOwners = (
    busFilters: BusFilters,
    setBusFilters: (busFilters: BusFilters) => void
): ((owners: ReadonlyArray<string> | null) => void) => {
    return (owners: ReadonlyArray<string> | null) => {
        setBusFilters({
            ...busFilters,
            owners
        });
    };
};

export {
    useCreateBusFiltersConfiguration,
    useCreateBusFiltersFromScreeningView
};
export type { BusFiltersConfiguration };
